import React, { useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./TripAI.css";
import MultiSelectModal from "./MultiSelectModal";
import PromptModal from "./PromptModal";
import { AUTHORIZATION_LEVEL } from "../../Common/Common";

// Sentinel item appended to each multi-select preset list — picking it
// opens a free-text prompt instead of adding a literal "__other__" value,
// so a preference not on the curated list can still be captured. Only on
// the four multi-select categories (Type/Traveling With/Focus/Activities);
// the single-pick fields (Budget/Pace/Transportation/Accessibility) keep
// their fixed options, since those are more deliberately constrained.
// Requested 2026-09-26.
const OTHER_VALUE = "__other__";
const otherOption = { label: "Other (please specify)", name: OTHER_VALUE, value: OTHER_VALUE, id: "other" };

// Standalone editor for Guide.aiPreferences — the preference set TripAI.js
// originally owned inline. Elevated the same way Destinations was: one
// persisted, independently-editable place instead of each consumer
// (AIDestinationsFlow.js, TripAI.js) owning its own copy of the same
// selectors. Both consumers now show a read-only summary + "Edit
// Preferences" link pointing here. Added 2026-08-28 — see memory:
// project_destinations_first_redesign_idea.
//
// Fields, after a 2026-08-28 content review:
// - TripType — trip STYLE/THEME only (beach, ski, safari, cultural...).
// - travelingWith — WHO's traveling (solo, family, honeymoon...), split out
//   from TripType, which used to conflate the two orthogonal questions.
// - focusOn / Activities / selectedBudget / selectedPace /
//   selectedTransportation — unchanged in kind, content reviewed for
//   overlap (wine/spa/wildlife/sport removed from TripType since they
//   duplicated Activities/Focus entries) and gaps (road trip, backpacking,
//   bleisure added to TripType; wellness, wildlife, national parks added to
//   Focus; golf/yoga/guided tours/winter sports added to Activities;
//   boat/ferry and on-foot added to Transportation).
// - includeExcursions — replaces the old "Proximity" field (for/in/near/in
//   or near), which was prompt grammar (search radius) rather than a
//   recognizable traveler preference. Same underlying need (day trips
//   beyond the destination city, e.g. Tuscany countryside/Pisa/Siena for a
//   Florence trip) reframed as a plain yes/no.
//
// Props: onHide() — closes without a separate onApply; Save writes directly
// onto window.guide_obj.aiPreferences, matching DestinationsScreen.js's own
// direct-write-then-close pattern for Guide.destinations.
const PreferencesScreen = ({ onHide }) => {
  const user_info = useSelector((state) => state.user_info);
  const Authorization = useSelector((state) => state.Authorization);
  const isGuestUser = user_info?.uid == null || user_info?.uid === "guest" || Authorization === AUTHORIZATION_LEVEL.GUEST;
  const saved = window.guide_obj?.aiPreferences;
  const [TripType, setTripType] = useState(saved?.TripType || "");
  const [TripTypeSelected, setTripTypeSelected] = useState(saved?.TripTypeSelected || [{ value: "" }]);
  const [travelingWith, setTravelingWith] = useState(saved?.travelingWith || "");
  const [travelingWithSelected, setTravelingWithSelected] = useState(saved?.travelingWithSelected || [{ value: "" }]);
  const [focusOn, setFocusOn] = useState(saved?.focusOn || "");
  const [focusONSelected, setFocusONSelected] = useState(saved?.focusONSelected || [{ value: "" }]);
  const [Activities, setActivities] = useState(saved?.Activities || "");
  const [ActivitiesSelected, setActivitiesSelected] = useState(saved?.ActivitiesSelected || [{ value: "" }]);
  const [selectedBudget, setSelectedBudget] = useState(saved?.selectedBudget || "");
  const [selectedPace, setSelectedPace] = useState(saved?.selectedPace || "");
  const [selectedTransportation, setSelectedTransportation] = useState(saved?.selectedTransportation || "");
  const [includeExcursions, setIncludeExcursions] = useState(saved?.includeExcursions ?? false);
  const [selectedAccessibility, setSelectedAccessibility] = useState(saved?.selectedAccessibility || "");
  const [multiSelect, setMultiSelect] = useState(null); // {items, selectedItems, mode, callback, title} | null
  const [error, setError] = useState("");
  const [PromptModalShow, setPromptModalShow] = useState(false);
  const [PromptModalTitle, setPromptModalTitle] = useState("Enter Value");
  const [PromptModalMessage, setPromptModalMessage] = useState("");
  const [PromptModalButtons, setPromptModalButtons] = useState([]);
  const [PromptModalInputs, setPromptModalInputs] = useState([]);

  const openMultiSelect = (items, selectedItems, mode, callback, title) => {
    setMultiSelect({ items, selectedItems, mode, callback, title });
  };

  // Wraps applyMultiSelection for the four categories that offer an "Other"
  // option: if the user checked it, prompt for the actual text and splice
  // it in as a real selected value (replacing the "__other__" sentinel)
  // before applying — otherwise behaves exactly like applyMultiSelection.
  const applyMultiSelectionWithOther = (selected, setSelectedList, setJoined, label) => {
    const withoutOther = selected.filter((s) => s.value !== OTHER_VALUE);
    if (withoutOther.length === selected.length) {
      applyMultiSelection(selected, setSelectedList, setJoined, label);
      return;
    }
    setPromptModalTitle(`Add your own ${label}`);
    setPromptModalMessage("");
    setPromptModalInputs([{ name: "", value: "" }]);
    setPromptModalButtons([
      {
        Title: "Add",
        Method: (inputs) => {
          const text = (inputs[0]?.value || "").trim();
          const finalSelected = text
            ? [...withoutOther, { label: text, name: text, value: text, id: `custom-${Date.now()}` }]
            : withoutOther;
          applyMultiSelection(finalSelected, setSelectedList, setJoined, label);
        },
      },
      { Title: "Cancel", Method: null },
    ]);
    setPromptModalShow(true);
  };

  // Enforces the same 10-item cap TripAI.js's original selectors had —
  // rejects the whole selection (leaving the prior one in place) rather than
  // silently truncating, matching the original UX. Was lost when these
  // selectors moved here from TripAI.js/AIDestinationsFlow.js — the "(10
  // max)" title survived but the actual enforcement didn't.
  const applyMultiSelection = (selected, setSelectedList, setJoined, label) => {
    if (selected.length > 10) {
      setError(`You can select up to 10 ${label}.`);
      return;
    }
    setError("");
    let joined = "";
    for (let i = 0; i < selected.length; i++) {
      if (joined !== "") joined += i === selected.length - 1 ? " and " : ", ";
      joined += selected[i].value;
    }
    setSelectedList(selected);
    setJoined(joined);
  };

  // ── Preference selectors ──────────────────────────────────────────────
  // TripType is trip theme/style only now — "who's traveling" moved to the
  // separate travelingWith field below (was conflated into one 38-item list
  // before). wine/spa/wildlife/sport removed — they duplicated Activities'
  // "wine tasting"/"spa treatments"/"sporting events" and Focus's future
  // "wildlife" entry; road trip/backpacking/bleisure added (popular trip
  // types that were missing).
  const selectTripType = () => {
    let types = [
      "adventure", "backpacking", "beach", "bleisure", "bucket list", "camping",
      "city break", "cruise", "culinary", "cultural", "cycling",
      "diving and snorkeling", "ecotourism", "escape", "excursion", "farmstay",
      "fashion", "fly-drive", "guided tour", "mountain", "pilgrimage",
      "religious", "road trip", "skiing",
    ].sort();
    openMultiSelect(
      [...types.map((t, i) => ({ label: t, name: t, value: t, id: (i + 1).toString() })), otherOption],
      TripTypeSelected,
      "multi",
      (selected) => applyMultiSelectionWithOther(selected, setTripTypeSelected, setTripType, "trip types"),
      "Select Type (10 max)"
    );
  };

  // Split out of TripType, 2026-08-28 — who's traveling is a different
  // question from trip theme (a "cultural" trip could be solo, a family, or
  // a honeymoon), and mixing both into one list made neither a clean signal
  // for the AI.
  const selectTravelingWith = () => {
    let options = [
      "babymoon", "boys-trip", "dog friendly", "family", "friends group",
      "girls-trip", "honeymoon", "kid friendly", "multi-generational",
      "pet friendly", "romantic", "seniors friendly", "solo",
    ].sort();
    openMultiSelect(
      [...options.map((t, i) => ({ label: t, name: t, value: t, id: (i + 1).toString() })), otherOption],
      travelingWithSelected,
      "multi",
      (selected) => applyMultiSelectionWithOther(selected, setTravelingWithSelected, setTravelingWith, "options"),
      "Select Who's Traveling (10 max)"
    );
  };

  // Added wellness and relaxation, wildlife, national parks (national parks
  // is more useful than generic "nature" alone).
  const selectFocus = () => {
    const focus = [
      "architecture", "art", "hidden gems", "history", "local cuisine",
      "music", "national parks", "nature", "nightlife",
      "off the beaten path", "photography", "shopping", "wellness and relaxation",
      "wildlife",
    ].sort();
    openMultiSelect(
      [...focus.map((t, i) => ({ label: t, name: t, value: t, id: (i + 1).toString() })), otherOption],
      focusONSelected,
      "multi",
      (selected) => applyMultiSelectionWithOther(selected, setFocusONSelected, setFocusOn, "focus areas"),
      "Select Focus (10 max)"
    );
  };

  // Added golf, yoga, guided walking tour, skiing, snowboarding — the list
  // had gotten noticeably thinner than it should be (e.g. TripType offers
  // "skiing" as a trip theme but Activities had zero winter-sport options).
  const selectActivities = () => {
    const activities = [
      "biking", "boating", "cooking class", "dancing", "diving", "fishing",
      "golf", "guided walking tour", "hiking", "live music", "museum visits",
      "safari", "skiing", "snowboarding", "spa treatments", "sporting events",
      "surfing", "swimming", "theater", "wildlife viewing", "wine tasting", "yoga",
    ].sort();
    openMultiSelect(
      [...activities.map((t, i) => ({ label: t, name: t, value: t, id: (i + 1).toString() })), otherOption],
      ActivitiesSelected,
      "multi",
      (selected) => applyMultiSelectionWithOther(selected, setActivitiesSelected, setActivities, "activities"),
      "Select Activities (10 max)"
    );
  };

  // Added by boat/ferry (coastal/island trips) and on foot (fully walkable
  // city trips, no transport needed).
  const selectTransportation = () => {
    const options = ["", "with a car", "without a car", "by train", "by bus", "by bike", "with a motorcycle", "by boat/ferry", "on foot"];
    openMultiSelect(
      options.map((t, i) => ({ label: t === "" ? "Any" : t, name: t, value: t, id: (i + 1).toString() })),
      [],
      "uni",
      (sel) => setSelectedTransportation(sel && sel.length > 0 ? sel[0].value : ""),
      "Select Transportation"
    );
  };

  // Added 2026-09-14 per user request — real gap: no way to tell TripAI/
  // Build My Trip a mobility constraint, so generated schedules could
  // include attractions/routes unreachable for the traveler.
  const selectAccessibility = () => {
    const options = ["", "limited mobility", "wheelchair accessible"];
    openMultiSelect(
      options.map((t, i) => ({ label: t === "" ? "No specific needs" : t, name: t, value: t, id: (i + 1).toString() })),
      [],
      "uni",
      (sel) => setSelectedAccessibility(sel && sel.length > 0 ? sel[0].value : ""),
      "Select Accessibility Needs"
    );
  };

  const selectBudget = () => {
    const options = ["", "very low cost", "low cost", "medium cost", "medium high cost", "expensive", "luxurious", "very luxurious"];
    openMultiSelect(
      options.map((t, i) => ({ label: t === "" ? "Any" : t, name: t, value: t, id: (i + 1).toString() })),
      [],
      "uni",
      (sel) => setSelectedBudget(sel && sel.length > 0 ? sel[0].value : ""),
      "Select Budget"
    );
  };

  const selectPace = () => {
    const options = ["", "slow paced", "relaxed", "fast moving", "busy"];
    openMultiSelect(
      options.map((t, i) => ({ label: t === "" ? "Any" : t, name: t, value: t, id: (i + 1).toString() })),
      [],
      "uni",
      (sel) => setSelectedPace(sel && sel.length > 0 ? sel[0].value : ""),
      "Select Pace"
    );
  };

  const handleSave = () => {
    window.guide_obj.aiPreferences = {
      TripType, TripTypeSelected, travelingWith, travelingWithSelected,
      focusOn, focusONSelected, Activities, ActivitiesSelected,
      selectedBudget, selectedPace, selectedTransportation, includeExcursions,
      selectedAccessibility,
    };
    // Flag this trip for persistence on sign-in — matches TripAI.js/
    // BuildMyTripWizard.js's own equivalent flag and DestinationsScreen.js's
    // identical fix. Without this, a guest who sets preferences here and
    // signs in BEFORE ever running a full TripAI/Build My Trip generation
    // (the only other places this flag got set) would have this edit
    // silently discarded on sign-in. Found 2026-08-28.
    if (isGuestUser) window.guest_generated_unsaved_trip = true;
    onHide();
  };

  const handleClearAll = () => {
    setTripType("");
    setTripTypeSelected([{ value: "" }]);
    setTravelingWith("");
    setTravelingWithSelected([{ value: "" }]);
    setFocusOn("");
    setFocusONSelected([{ value: "" }]);
    setActivities("");
    setActivitiesSelected([{ value: "" }]);
    setSelectedBudget("");
    setSelectedPace("");
    setSelectedTransportation("");
    setIncludeExcursions(false);
    setSelectedAccessibility("");
    setError("");
  };

  return (
    <>
      {multiSelect && (
        <MultiSelectModal
          show={true}
          onHide={() => setMultiSelect(null)}
          MultiselectItems={multiSelect.items}
          MultiselectSelectedItems={multiSelect.selectedItems}
          MultiselectMode={multiSelect.mode}
          MultiselectCallback={(selected) => { const cb = multiSelect.callback; setMultiSelect(null); cb(selected); }}
          MultiselectTitle={multiSelect.title}
          MultiselectSearchText=""
        />
      )}

      {PromptModalShow && (
        <PromptModal
          show={PromptModalShow}
          onHide={() => setPromptModalShow(false)}
          PromptModalTitle={PromptModalTitle}
          PromptModalMessage={PromptModalMessage}
          PromptModalButtons={PromptModalButtons}
          PromptModalInputs={PromptModalInputs}
        />
      )}

      {!multiSelect && !PromptModalShow && (
        <div className="tai-paste-overlay" onClick={onHide}>
          <div className="tai-paste-modal" onClick={(e) => e.stopPropagation()}>
            <p className="tai-paste-title">Preferences</p>
            <p className="tai-paste-hint">
              Used by "Destinations from AI" and "Build Trip with AI" — set once, applies to both.
            </p>
            {error !== "" && <p className="tai-paste-error">{error}</p>}
            <div className="tai-row-item" onClick={selectTripType}>
              <span className="tai-row-key">Type</span>
              {TripType ? <span className="tai-row-value">{TripType}</span> : null}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-row-item" onClick={selectTravelingWith}>
              <span className="tai-row-key">Traveling With</span>
              {travelingWith ? <span className="tai-row-value">{travelingWith}</span> : null}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-row-item" onClick={selectFocus}>
              <span className="tai-row-key">Focus</span>
              {focusOn ? <span className="tai-row-value">{focusOn}</span> : null}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-row-item" onClick={selectActivities}>
              <span className="tai-row-key">Activities</span>
              {Activities ? <span className="tai-row-value">{Activities}</span> : null}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-row-item" onClick={selectBudget}>
              <span className="tai-row-key">Budget</span>
              {selectedBudget ? <span className="tai-row-value">{selectedBudget}</span> : null}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-row-item" onClick={selectPace}>
              <span className="tai-row-key">Pace</span>
              {selectedPace ? <span className="tai-row-value">{selectedPace}</span> : null}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-row-item" onClick={selectTransportation}>
              <span className="tai-row-key">Transportation</span>
              {selectedTransportation ? <span className="tai-row-value">{selectedTransportation}</span> : null}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-row-item" onClick={selectAccessibility}>
              <span className="tai-row-key">Accessibility</span>
              {selectedAccessibility ? <span className="tai-row-value">{selectedAccessibility}</span> : null}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-check-row" onClick={() => setIncludeExcursions(!includeExcursions)}>
              <div className={`tai-check-box${includeExcursions ? " checked" : ""}`}>
                {includeExcursions && <FontAwesomeIcon icon={solid("check")} />}
              </div>
              <span className="tai-check-label">Include day-trip excursions to nearby towns</span>
            </div>

            <div className="tai-paste-actions">
              <button className="tai-cancel-btn" onClick={handleClearAll}>Clear All</button>
              <button className="tai-cancel-btn" onClick={onHide}>Cancel</button>
              <button className="tai-primary-btn" onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PreferencesScreen;
